// FormActions.jsx - Updated to use centralized configuration
import axios from 'axios';
import { BASE_URL } from '../../../../Constants/constants';
import {
    IDENTIFICATION_REQUIRED_FIELDS,
    IDENTIFICATION_FORM_FIELDS,
    IDENTIFICATION_CONDITIONAL_RULES
} from './identificationConfig';

/**
 * Creates an authentication configuration object for API requests
 */
const getAuthConfig = () => {
    const token = localStorage.getItem('accessToken');
    console.log("Auth token present:", !!token);

    if (!token) {
        console.warn("No access token found in localStorage!");
    }

    return {
        headers: {
            Authorization: `Bearer ${token || ''}`,
            'Content-Type': 'application/json',
        },
    };
};

/**
 * Fetches existing identification documents for the current user
 */
export const fetchIdentificationDocuments = async () => {
    try {
        console.log('Fetching identification documents from API...');
        console.log('API URL:', `${BASE_URL}/identification_documents/me`);

        const config = getAuthConfig();
        const response = await axios.get(
            `${BASE_URL}/identification_documents/me`,
            config
        );

        console.log('Fetched identification documents response:', {
            status: response.status,
            statusText: response.statusText,
            data: response.data
        });

        const normalizedData = normalizeDocumentData(response.data);
        console.log('Normalized data:', normalizedData);

        return normalizedData;
    } catch (error) {
        if (error.response?.status === 404) {
            console.log('No identification documents found (404 response)');
            return null;
        }

        console.error('Error fetching identification documents:', error);

        if (error.message === 'Network Error') {
            throw new Error('Network error. Please check your internet connection and try again.');
        }

        throw new Error(
            error.response?.data?.detail ||
            error.message ||
            'Failed to fetch identification documents.'
        );
    }
};

/**
 * Normalizes document data to ensure consistent structure
 */
const normalizeDocumentData = (data) => {
    if (!data) return null;

    const defaultFields = {
        id: null,
        user_id: null,
        national_passport: '',
        national_passport_expiry_date: '',
        drivers_license: '',
        drivers_license_expiry_date: '',
        diplomatic_card: '',
        diplomatic_card_expiry_date: '',
        national_id_card: '',
        national_id_card_expiry_date: '',
        unlp: '',
        unlp_expiry_date: '',
        country_of_issue: '',
        issue_date: '',
        expiration_date: ''
    };

    const normalizedData = { ...defaultFields };

    Object.keys(data).forEach(key => {
        if (key in normalizedData) {
            if (key.includes('_date') || key === 'issue_date' || key === 'expiration_date') {
                normalizedData[key] = data[key];
            } else if (typeof defaultFields[key] === 'string') {
                normalizedData[key] = data[key] === null || data[key] === undefined ? '' : data[key];
            } else {
                normalizedData[key] = data[key];
            }
        }
    });

    return normalizedData;
};

/**
 * Saves or updates an identification document
 */
export const saveIdentificationDocument = async (formData, currentUser) => {
    try {
        const payload = sanitizePayload({
            ...formData,
            user_id: currentUser?.id
        });

        console.log('Preparing to save identification document:', payload);

        if (!payload.user_id) {
            console.error('Missing user_id in payload!');
            throw new Error('User ID is required to save document.');
        }

        let response;
        if (payload.id) {
            console.log(`Updating document with ID: ${payload.id}`);
            response = await axios.put(
                `${BASE_URL}/identification_documents/${payload.id}`,
                payload,
                getAuthConfig()
            );
        } else {
            console.log('Creating new document');
            response = await axios.post(
                `${BASE_URL}/identification_documents/`,
                payload,
                getAuthConfig()
            );
        }

        console.log('Save operation successful:', {
            status: response.status,
            statusText: response.statusText,
            data: response.data
        });

        const normalizedData = normalizeDocumentData(response.data);
        return normalizedData;
    } catch (error) {
        console.error('Error in save operation:', error);

        if (error.response) {
            const errorMessage = error.response.data?.detail ||
                'Failed to save identification document.';
            console.error('Server error response:', {
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data
            });
            throw new Error(errorMessage);
        } else if (error.request) {
            console.error('Network error - no response received:', error.request);
            throw new Error('Network error. Please check your connection.');
        } else {
            console.error('Error in request setup:', error.message);
            throw new Error(error.message || 'An error occurred while saving the document.');
        }
    }
};

/**
 * Deletes an identification document
 */
export const deleteIdentificationDocument = async (documentId) => {
    if (!documentId) {
        console.error('Cannot delete: Missing document ID');
        throw new Error('Document ID is required for deletion.');
    }

    try {
        console.log(`Attempting to delete document with ID: ${documentId}`);

        const response = await axios.delete(
            `${BASE_URL}/identification_documents/${documentId}`,
            getAuthConfig()
        );

        console.log('Delete operation successful:', {
            status: response.status,
            statusText: response.statusText,
            data: response.data
        });

        return response.data;
    } catch (error) {
        console.error('Error deleting document:', error);

        if (error.response) {
            if (error.response.status === 404) {
                throw new Error('Document not found. It may have been already deleted.');
            } else if (error.response.status === 403) {
                throw new Error('You do not have permission to delete this document.');
            }
        }

        throw new Error(
            error.response?.data?.detail ||
            error.message ||
            'Failed to delete identification document.'
        );
    }
};

/**
 * Enhanced validation function using centralized configuration
 * Includes conditional validation and empty form detection
 */
export const validateIdentificationFormData = (formData) => {
    if (!formData) {
        console.log("validateIdentificationFormData called with null/undefined formData");
        return {
            errors: {},
            percentage: 0,
            isEmpty: true
        };
    }

    // Use centralized configuration - NO DUPLICATION!
    const errors = {};

    // Check if form is completely empty
    const isEmpty = isFormEmpty(formData);
    // Note: We don't add an error for empty form since button will be disabled

    // Check required fields using imported configuration
    Object.entries(IDENTIFICATION_REQUIRED_FIELDS).forEach(([field, isRequired]) => {
        if (isRequired) {
            const value = formData[field];
            if (!value || value.toString().trim() === '') {
                errors[field] = `${field.replace(/_/g, ' ')} is required`;
            }
        }
    });

    // Conditional validation - if ID is provided, expiry date is required
    Object.entries(IDENTIFICATION_CONDITIONAL_RULES).forEach(([idField, expiryField]) => {
        const idValue = formData[idField];
        const expiryValue = formData[expiryField];

        // If ID field has a value but expiry field is empty
        if (idValue && idValue.toString().trim() !== '' &&
            (!expiryValue || expiryValue.toString().trim() === '')) {
            errors[expiryField] = `${expiryField.replace(/_/g, ' ')} is required when ${idField.replace(/_/g, ' ')} is provided`;
        }
    });

    // Calculate percentage using centralized field list
    let filledFieldsCount = 0;
    const totalFields = IDENTIFICATION_FORM_FIELDS.length;

    console.log("Checking identification form fields:");

    IDENTIFICATION_FORM_FIELDS.forEach(field => {
        const value = formData[field];
        const isFilled = value !== null && value !== undefined && String(value).trim() !== '';

        console.log(`  Field ${field}: ${isFilled ? 'FILLED' : 'EMPTY'}`);

        if (isFilled) {
            filledFieldsCount++;
        }
    });

    const percentage = totalFields > 0 ? Math.round((filledFieldsCount / totalFields) * 100) : 0;
    console.log(`Form completion: ${filledFieldsCount}/${totalFields} fields = ${percentage}%`);
    console.log(`Validation errors found: ${Object.keys(errors).length}`);
    console.log(`Form is empty: ${isEmpty}`);

    return {
        errors,
        percentage,
        isEmpty
    };
};

/**
 * Helper function to check if the form is completely empty
 * Uses centralized field list from config
 */
const isFormEmpty = (formData) => {
    if (!formData) return true;

    // Use centralized field list instead of hardcoded array
    const hasAnyData = IDENTIFICATION_FORM_FIELDS.some(field => {
        const value = formData[field];
        return value && value.toString().trim() !== '';
    });

    return !hasAnyData;
};

/**
 * Sanitizes the form data before sending to the API
 */
const sanitizePayload = (data) => {
    if (!data) {
        console.warn('sanitizePayload called with null/undefined data');
        return {};
    }

    const sanitizedData = { ...data };

    Object.keys(sanitizedData).forEach((key) => {
        const value = sanitizedData[key];

        if (typeof value === 'string') {
            sanitizedData[key] = value.trim() === '' ? null : value.trim();
        } else if (value === undefined) {
            sanitizedData[key] = null;
        }
    });

    if (!sanitizedData.user_id) {
        console.warn('Warning: Missing user_id in payload');
    }

    console.log('Sanitized payload:', sanitizedData);
    return sanitizedData;
};